/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import i18next from 'i18next';
import { merge } from 'lodash';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { startApplicationLoading, stopApplicationLoading } from '../features/application/application.slice';
import { createMessage } from '../features/alerts/alerts.helper';
import { postSignOutThunk } from '../features/auth/auth.thunk';
import createDebouncedAsyncThunk from './thunk.debounced';

const defaultOptions = {
  errorMessage: undefined,
  showApplicationLoading: false,
  wait: 0, // used for debouncing the thunk
};

/**
 * Customized createdAsyncThunk that throws an errorMessage error when it fails
 * and, optionally, can be debounced via options.wait
 *
 * Usage:
 *    Case 1: If the payloadCreator should only throw 1 error
 *      then use errorMessage to set the title and message of that error (override or use the default value)
 *    Case 2: If the payloadCreator should throw multiple errors
 *      then use thunkApi.rejectWithValue in your payloadCreator to throw multiple errors
 */
export const createFOAsyncThunk = (type, payloadCreator, options = defaultOptions) => {
  let _errorMessage = i18next.t('error.page.generic.message');
  // resources need to be loaded in order to translate the default message, otherwise fallback to hardcoded message
  if (!_errorMessage || _errorMessage === 'error.page.generic.message') {
    _errorMessage = i18next.t('messages.contact.local.support');
  }
  const _options = merge(
    {
      errorMessage: _errorMessage,
      wait: 0,
    },
    options,
  );

  const _payloadCreator = async (arg, thunkApi) => {
    try {
      if (_options.showApplicationLoading) {
        thunkApi.dispatch(startApplicationLoading());
      }
      return await payloadCreator(arg, thunkApi);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      if (error?.code === 'ERR_CANCELED') {
        // eslint-disable-next-line consistent-return
        return;
      }
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        thunkApi.dispatch(postSignOutThunk());
      }

      if (error?.response?.status === 409) {
        return thunkApi.rejectWithValue({ showAlert: false, error: { status: 409 } });
      }

      return thunkApi.rejectWithValue(createMessage(await getMessageFromError(error), error));
    } finally {
      if (_options.showApplicationLoading) {
        thunkApi.dispatch(stopApplicationLoading());
      }
    }
  };
  if (_options.wait > 0) return createDebouncedAsyncThunk(type, payloadCreator, _options.wait);
  return createAsyncThunk(type, _payloadCreator);
};

export async function getMessageFromError(error, defaultMessage = i18next.t('error.page.generic.message')) {
  let message = error?.data?.message || error?.response?.data?.message;
  let params = error?.data?.params || error?.response?.data?.params;

  if (!message && error?.data?.size > 0 && error?.data instanceof Blob) {
    const dataFromBlob = JSON.parse(await error?.data.text());
    message = dataFromBlob?.message;
    params = dataFromBlob?.params;
  }

  if (message) {
    message = i18next.t(message, params);
  } else {
    message = defaultMessage;
  }

  return message;
}
